import { Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentDetail, TournamentDetailResponse, TournamentLayoutDetail as TournamentLayoutDetailModel } from "../api";
import { renderTournamentStatus, renderTournamentValue, TournamentEmptyState, TournamentJsonBlock, TournamentKeyValueGrid, TournamentPageHeader, TournamentSection, TournamentSourceNotice } from "../components";

export const TournamentLayoutDetail = (props: any) => {
    const layoutId = props.match?.params?.layoutId;
    const [response, setResponse] = React.useState<TournamentDetailResponse<TournamentLayoutDetailModel> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Layout");
        loadDetail();
    }, [layoutId]);

    const loadDetail = () => {
        setLoading(true);
        setError(null);

        fetchTournamentDetail<TournamentLayoutDetailModel>(`/api/tournaments/layouts/${layoutId}`, props.activeEnv)
            .then(data => {
                setResponse(data);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament layout detail.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const item = response?.data.item;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Layout / Detail"
            action={<Button disableElevation variant="contained" color="primary" onClick={loadDetail}>Refresh</Button>}
        />

        <div style={{ marginTop: 16 }}>
            <TournamentSourceNotice meta={response?.meta} />
        </div>

        <LoadingWrapper loading={loading} error={error}>
            {!response?.data.found || !item ? <TournamentEmptyState
                title="Layout not found"
                description={`No tournament layout is available for ID ${layoutId}.`}
            /> : <React.Fragment>
                <TournamentSection
                    title="Summary"
                    action={renderTournamentStatus(item.status)}
                >
                    <TournamentKeyValueGrid
                        items={[
                            { label: "Layout ID", value: item.id },
                            { label: "Name", value: renderTournamentValue(item.name) },
                            { label: "Difficulty", value: renderTournamentValue(item.difficulty) },
                            { label: "Environment ID", value: renderTournamentValue(item.environmentId) },
                            { label: "Map size", value: renderTournamentValue(item.mapSize) },
                            { label: "Category", value: renderTournamentValue(item.category) },
                            { label: "Usage count", value: item.usageCount },
                            { label: "Updated at", value: item.updatedAt ? Helpers.formatDateWithTime(item.updatedAt, true) : "n/a" },
                        ]}
                    />
                </TournamentSection>

                <TournamentSection title="Metadata">
                    <TournamentKeyValueGrid
                        items={Object.entries(item.metadata || {}).map(([label, value]) => ({
                            label,
                            value: renderTournamentValue(value),
                        }))}
                    />
                </TournamentSection>

                <TournamentSection title="Placements">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>X</TableCell>
                                <TableCell>Y</TableCell>
                                <TableCell>Level</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {item.placements.map((placement, index) => <TableRow key={`${item.id}-placement-${index}`}>
                                <TableCell>{renderTournamentValue(placement.type)}</TableCell>
                                <TableCell>{renderTournamentValue(placement.x)}</TableCell>
                                <TableCell>{renderTournamentValue(placement.y)}</TableCell>
                                <TableCell>{renderTournamentValue(placement.level)}</TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TournamentSection>

                <TournamentSection title="Rules">
                    <TournamentKeyValueGrid
                        items={Object.entries(item.rules || {}).map(([label, value]) => ({
                            label,
                            value: Array.isArray(value) ? value.join(", ") : renderTournamentValue(value),
                        }))}
                    />
                </TournamentSection>

                <TournamentSection title="Linked Tournaments">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tournament</TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>Rounds</TableCell>
                                <TableCell>Usage count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {item.linkedTournaments.map(tournament => <TableRow key={tournament.id}>
                                <TableCell>{tournament.name}</TableCell>
                                <TableCell>{tournament.id}</TableCell>
                                <TableCell>{tournament.rounds.join(", ")}</TableCell>
                                <TableCell>{tournament.usageCount}</TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>
                </TournamentSection>

                <TournamentJsonBlock title="Raw Payload" data={item.rawPayload || {}} />
            </React.Fragment>}
        </LoadingWrapper>
    </div>;
};
